import { useRef, useState } from "react";
import { initialsFor } from "../../lib/initials";
import { relationshipColor } from "../../lib/relationshipColors";
import { photoRejectionReason } from "../../lib/imageResize";

/** The hero avatar with a camera badge that opens the file picker. */
export default function PhotoUpload({
  photoUrl,
  name,
  relationshipType,
  onUpload,
  onRemove,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reason = photoRejectionReason(file);
    if (reason) {
      setError(reason);
      return;
    }
    setError("");
    setBusy(true);
    try {
      await onUpload?.(file);
    } catch (uploadError) {
      setError(uploadError?.message || "That photo could not be uploaded.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setError("");
    try {
      await onRemove?.();
    } catch (removeError) {
      setError(removeError?.message || "That photo could not be removed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="photo-upload">
      <div className="photo-upload-frame">
        <div
          className="photo-portrait"
          style={photoUrl ? undefined : { background: relationshipColor(relationshipType) }}
        >
          {photoUrl ? <img src={photoUrl} alt={name} /> : (
            <span className="photo-initials">{initialsFor(name)}</span>
          )}
          {busy && <div className="photo-upload-busy" aria-hidden="true" />}
        </div>
        <button
          type="button"
          className="photo-upload-action"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          aria-label={photoUrl ? `Change photo of ${name}` : `Add a photo of ${name}`}
          title={photoUrl ? "Change photo" : "Add photo"}
        >
          {photoUrl ? "Replace" : "Add photo"}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="visually-hidden"
        onChange={handleFile}
        tabIndex={-1}
      />
      {photoUrl && !busy && (
        <button type="button" className="photo-upload-remove link-button" onClick={handleRemove}>
          Remove
        </button>
      )}
      {error && (
        <div className="photo-upload-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
